import { EnvelopeSimple, Paperclip, WarningCircle, X } from '@phosphor-icons/react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'urql'
import { EmailBodyEditor } from 'app/components/contacts/inbox/EmailBodyEditor'
import { LOCATION_CAN_SEND_EMAIL_QUERY } from 'app/components/contacts/inbox/graphql'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import {
  EMAIL_ACCEPT,
  EMAIL_MAX_FILES,
  EMAIL_NO_CONTACT,
  EMAIL_NOT_READY_CODES,
  EMAIL_SUBJECT_MAX,
  attachmentError,
  emailRefusalReason,
  formatFileSize,
  resolveEmailContentType,
} from 'lib/emailAttachments'
import { cn } from 'app/lib/utils'
import { notifyError } from 'app/services/notify.client'
import { useRootStore } from 'app/stores/StoreContext'
import type { Message } from 'app/generated/graphql'
import type { ContactJson } from 'app/types/entities'
import { logError } from 'lib/logging'

const LABEL_CLASS = `w-14 shrink-0 text-body-sm font-normal`

type Attachment = {
  key: string
  file: File
  contentType: string
  progress: number
  base64?: string
  error?: string
}

export const EmailComposer = observer(function EmailComposer({
  contact,
  locationId,
  onMessage,
}: {
  contact?: Pick<ContactJson, 'id' | 'email'>
  locationId?: string
  onMessage: (message: Message) => void
}) {
  const { messages } = useRootStore()
  const fieldId = useId()
  const readinessContext = useMemo(() => ({ suppressErrorToast: true }), [])
  const [readiness] = useQuery({
    query: LOCATION_CAN_SEND_EMAIL_QUERY,
    variables: { locationId: locationId ?? `` },
    pause: !locationId,
    requestPolicy: `cache-and-network`,
    context: readinessContext,
  })
  const readinessCode = readiness.error?.graphQLErrors[0]?.extensions?.code
  const notReadyCode =
    typeof readinessCode === `string` && EMAIL_NOT_READY_CODES.has(readinessCode) ? readinessCode : undefined
  const contactEmail = contact?.email ?? ``
  const [to, setTo] = useState(contactEmail)
  const [prefilledEmail, setPrefilledEmail] = useState(contactEmail)
  if (contactEmail !== prefilledEmail) {
    setPrefilledEmail(contactEmail)
    if (to === prefilledEmail) setTo(contactEmail)
  }
  const [subject, setSubject] = useState(``)
  const [body, setBody] = useState({ html: ``, isEmpty: true })
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [sendProgress, setSendProgress] = useState<number | undefined>(undefined)
  const [refusal, setRefusal] = useState<string | undefined>(undefined)
  const latestAttachments = useRef<Attachment[]>([])
  const fileInput = useRef<HTMLInputElement>(null)
  const clearBody = useRef<() => void>(() => {})
  // Keyed by contact, so an unmount means the viewer moved to another conversation mid-send.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const onBodyChange = useCallback((html: string, isEmpty: boolean) => setBody({ html, isEmpty }), [])
  const onBodyReady = useCallback((clear: () => void) => {
    clearBody.current = clear
  }, [])

  // The composer renders inside the SMS reply form, so an Enter here would submit the SMS draft.
  const blockEnterSubmit = (evt: KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === `Enter`) evt.preventDefault()
  }

  const accepted = attachments.filter((a) => !a.error)
  const acceptedBytes = accepted.reduce((sum, a) => sum + a.file.size, 0)
  const encodedBytes = accepted.reduce((sum, a) => sum + (a.base64?.length ?? 0), 0)
  const sending = sendProgress !== undefined
  const canSend =
    !sending &&
    accepted.length === attachments.length &&
    accepted.every((a) => a.base64 !== undefined) &&
    Boolean(to.trim() && subject.trim()) &&
    !body.isEmpty

  // Limits are checked against the latest list, not this render's, so attaches that land before a re-render
  // still share one 10-file, 10 MB budget.
  const updateAttachments = (next: (prev: Attachment[]) => Attachment[]) => {
    latestAttachments.current = next(latestAttachments.current)
    setAttachments(latestAttachments.current)
  }

  const patch = (key: string, changes: Partial<Attachment>) =>
    updateAttachments((prev) => prev.map((a) => (a.key === key ? { ...a, ...changes } : a)))

  const read = (attachment: Attachment) => {
    const reader = new FileReader()
    reader.onprogress = (evt) => {
      if (evt.lengthComputable) patch(attachment.key, { progress: evt.loaded / evt.total })
    }
    reader.onload = () => patch(attachment.key, { progress: 1, base64: String(reader.result).split(`,`)[1] ?? `` })
    reader.onerror = () => {
      logError(`EmailComposer could not read ${attachment.file.name}`, reader.error)
      patch(attachment.key, { error: `This file couldn't be read.` })
    }
    reader.readAsDataURL(attachment.file)
  }

  const attach = (files: FileList | File[] | null) => {
    if (!files || sending) return
    const current = latestAttachments.current.filter((a) => !a.error)
    const totals = { count: current.length, bytes: current.reduce((sum, a) => sum + a.file.size, 0) }
    const added = [...files].map<Attachment>((file) => {
      const error = attachmentError(file, totals)
      if (!error) {
        totals.count += 1
        totals.bytes += file.size
      }
      return {
        key: `${file.name}-${file.size}-${Math.random()}`,
        file,
        contentType: resolveEmailContentType(file) ?? ``,
        progress: 0,
        error,
      }
    })
    updateAttachments((prev) => [...prev, ...added])
    added.filter((a) => !a.error).forEach(read)
  }

  if (notReadyCode && locationId) return <NotReadyBanner code={notReadyCode} locationId={locationId} />

  const send = async () => {
    if (!canSend) return
    setRefusal(undefined)
    if (!contact?.id || !locationId) {
      setRefusal(EMAIL_NO_CONTACT)
      return
    }
    setSendProgress(0)
    const result = await messages.sendEmail(
      {
        contactId: contact.id,
        locationId,
        to: to.trim(),
        subject: subject.trim(),
        html: body.html,
        attachments: accepted.map((a) => ({
          filename: a.file.name,
          contentType: a.contentType,
          base64: a.base64 ?? ``,
        })),
      },
      setSendProgress,
    )
    if (!result.ok) logError(`sendContactEmail failed`, result.code, result.reason)
    if (!mounted.current) {
      if (!result.ok) notifyError(`Email not sent. ${result.reason}`)
      return
    }
    setSendProgress(undefined)
    if (!result.ok) {
      setRefusal(result.reason)
      return
    }
    setSubject(``)
    updateAttachments(() => [])
    clearBody.current()
    onMessage(result.message)
  }

  return (
    <div className="flex flex-col gap-2">
      {refusal && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-danger-subtle bg-danger-minimal px-3 py-2 text-body-sm text-danger-strong"
        >
          <WarningCircle className="mt-0.5 h-4 w-4 shrink-0" weight="fill" />
          <span className="flex-1">Not sent. {refusal} Your message and files are still here.</span>
        </div>
      )}
      <div
        className="flex flex-col rounded-md border-2 border-primary-200 bg-surface shadow-elevation-1 transition-all focus-within:border-primary-400 focus-within:ring-4 focus-within:ring-primary-100"
        onDragOver={(evt) => evt.preventDefault()}
        onDrop={(evt) => {
          evt.preventDefault()
          attach(evt.dataTransfer.files)
        }}
      >
        <div className="flex items-center gap-2 border-b border-default px-3 py-1.5 text-body-sm text-subtle">
          <Label htmlFor={`${fieldId}-to`} className={LABEL_CLASS}>
            To
          </Label>
          <Input
            id={`${fieldId}-to`}
            type="text"
            inputMode="email"
            autoComplete="email"
            value={to}
            readOnly={sending}
            onKeyDown={blockEnterSubmit}
            onChange={(evt) => setTo(evt.target.value)}
            placeholder={contact?.email ? undefined : `No email on file. Enter an address.`}
            variant="bare"
            className="h-8"
          />
        </div>
        <div className="flex items-center gap-2 border-b border-default px-3 py-1.5 text-body-sm text-subtle">
          <Label htmlFor={`${fieldId}-subject`} className={LABEL_CLASS}>
            Subject
          </Label>
          <Input
            id={`${fieldId}-subject`}
            value={subject}
            maxLength={EMAIL_SUBJECT_MAX}
            readOnly={sending}
            onKeyDown={blockEnterSubmit}
            onChange={(evt) => setSubject(evt.target.value)}
            variant="bare"
            className="h-8"
          />
        </div>
        <EmailBodyEditor editable={!sending} onChange={onBodyChange} onReady={onBodyReady} />
        {attachments.length > 0 && (
          <ul className="flex flex-wrap gap-2 px-3 pb-2">
            {attachments.map((a) => (
              <li
                key={a.key}
                className={cn(
                  `flex min-w-40 max-w-full flex-col gap-1 rounded-md border px-2 py-1.5 text-body-xs`,
                  a.error ? `border-danger-subtle bg-danger-minimal` : `border-default bg-surface-subtle`,
                )}
              >
                <div className="flex items-center gap-1">
                  <span className="min-w-0 flex-1 truncate font-medium text-strong">{a.file.name}</span>
                  <Button
                    type="button"
                    variant="subtle"
                    size="icon-sm"
                    aria-label={`Remove ${a.file.name}`}
                    disabled={sending}
                    onClick={() => updateAttachments((prev) => prev.filter((p) => p.key !== a.key))}
                    className="size-5"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                {a.error ? (
                  <span className="text-danger-strong">{a.error}</span>
                ) : a.base64 === undefined ? (
                  <ProgressBar fraction={a.progress} />
                ) : (
                  <span className="text-subtle">{formatFileSize(a.file.size)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        {sending && (
          <div className="flex flex-col gap-1 px-3 pb-2 text-body-xs text-subtle">
            <span>
              {accepted.length > 0
                ? `Sending ${accepted.length} ${accepted.length === 1 ? `file` : `files`}, ${formatFileSize(encodedBytes * sendProgress)} of ${formatFileSize(encodedBytes)}`
                : `Sending`}
            </span>
            <ProgressBar fraction={sendProgress} />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-default px-2 py-2">
          <Input
            ref={fileInput}
            type="file"
            multiple
            wrapperClassName="hidden"
            accept={EMAIL_ACCEPT}
            onChange={(evt) => {
              attach(evt.target.files)
              evt.target.value = ``
            }}
          />
          <Button
            type="button"
            variant="iconFilled"
            size="icon"
            title="Attach files"
            aria-label="Attach files"
            disabled={sending}
            onClick={() => fileInput.current?.click()}
          >
            <Paperclip className="h-5 w-5" />
          </Button>
          {accepted.length > 0 && (
            <span className="text-body-xs text-subtle">
              {accepted.length} of {EMAIL_MAX_FILES} files, {formatFileSize(acceptedBytes)} of 10 MB
            </span>
          )}
          <Button type="button" variant="primary" size="md" disabled={!canSend} onClick={send} className="ml-auto">
            {sending ? `Sending…` : `Send email`}
          </Button>
        </div>
      </div>
    </div>
  )
})

const FIX_LINKS: Record<string, { label: string; href: (locationId: string) => string }> = {
  LOCATION_NOT_CONFIGURED: {
    label: `Set up email`,
    href: (locationId) => `/settings/location-settings/${locationId}/email`,
  },
  DOMAIN_NOT_VERIFIED: { label: `Check domain`, href: () => `/settings/sending-domains` },
}

function NotReadyBanner({ code, locationId }: { code: string; locationId: string }) {
  const checkPermission = useCheckPermission()
  const fix = checkPermission(`settings:workspace`) ? FIX_LINKS[code] : undefined
  const nextStep =
    code === `WORKSPACE_SENDING_PAUSED`
      ? ` Contact Trackable support to resume it.`
      : fix
        ? ``
        : ` Ask a workspace admin to set it up.`
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border-2 border-default bg-surface-muted/60 py-2.5 pl-3 pr-2">
      <span
        aria-hidden
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-strong text-subtle"
      >
        <EnvelopeSimple className="h-4 w-4" />
      </span>
      <span className="min-w-40 flex-1 text-body-sm font-medium leading-5 tracking-wide text-default">
        {emailRefusalReason(code, undefined)}
        {nextStep}
      </span>
      {fix && (
        <Button asChild variant="white" size="md" shape="pill" className="ml-auto">
          <Link to={fix.href(locationId)}>{fix.label}</Link>
        </Button>
      )}
    </div>
  )
}

function ProgressBar({ fraction }: { fraction: number }) {
  return (
    <span className="h-1 overflow-hidden rounded bg-surface-strong">
      <span
        className="block h-1 w-(--progress) bg-primary-600"
        style={{ '--progress': `${Math.round(fraction * 100)}%` } as CSSProperties}
      />
    </span>
  )
}
